// Product gallery carousel: one slide at a time in a native scroll-snap track.
// Commands: --prev / --next / --go (button carries data-index). Tracks the visible slide to sync thumbnails and the counter.
class SProductCarousel extends HTMLElement {
  #controller = null;
  #observer = null;
  #index = 0;

  get #track() { return this.querySelector('[data-carousel-track]'); }
  get #slides() { return [...(this.#track?.children ?? [])]; }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('command', (event) => {
      const count = this.#slides.length;
      if (!count) return;
      if (event.command === '--prev') this.#go((this.#index - 1 + count) % count);
      if (event.command === '--next') this.#go((this.#index + 1) % count);
      if (event.command === '--go') this.#go(Number(event.source?.dataset.index ?? 0));
    }, { signal });

    this.#track?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.#go(Math.min(this.#index + 1, this.#slides.length - 1)); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.#go(Math.max(this.#index - 1, 0)); }
    }, { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#setActive(this.#slides.indexOf(entry.target));
        }
      }
    }, { root: this.#track, threshold: [0.6] });
    for (const slide of this.#slides) this.#observer.observe(slide);
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #go(index) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const left = isRtl ? -(track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    for (const thumb of this.querySelectorAll('[data-carousel-thumb]')) {
      const active = Number(thumb.dataset.index) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.parentElement?.closest('[data-carousel-thumbs]');
        if (strip) {
          const offset = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
          strip.scrollTo({ left: offset, behavior: 'auto' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
    const counter = this.querySelector('[data-carousel-current]');
    if (counter) counter.textContent = String(index + 1);
  }
}

if (!customElements.get('s-product-carousel')) customElements.define('s-product-carousel', SProductCarousel);
